import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, ShieldCheck, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Controller, useFieldArray, useForm, type UseFormReturn } from 'react-hook-form';
import { Button, Checkbox, FormField, Input } from '@payment-plan/ui';
import { EYEBROW_CLASS, NATIVE_SELECT_CLASS, SECTION_CARD_CLASS, TOUCH_INPUT_CLASS } from './field-styles';
import { mensajeDeError } from '../errors';
import {
  ESTADOS_CIVILES,
  TIPOS_DOCUMENTO_NATURAL,
  applicantsFormSchema,
  emptyApplicant,
  toApplicantInputs,
  type ApplicantsFormValues,
} from '../separation.schema';
import { useSaveApplicantsWithAuthorization } from '../use-separation-flow';

interface ApplicantsStepProps {
  separacionId: string;
  initialValues?: ApplicantsFormValues;
  onDone: (values: ApplicantsFormValues) => void;
}

export function ApplicantsStep({ separacionId, initialValues, onDone }: ApplicantsStepProps): React.JSX.Element {
  const form = useForm<ApplicantsFormValues>({
    resolver: zodResolver(applicantsFormSchema),
    defaultValues: initialValues ?? { separantes: [emptyApplicant('100')], autorizacion: false },
  });
  const { control, handleSubmit, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'separantes' });
  const guardar = useSaveApplicantsWithAuthorization();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const varios = fields.length > 1;

  const onSubmit = (values: ApplicantsFormValues): void => {
    setServerError(null);
    guardar.mutate(
      { id: separacionId, separantes: toApplicantInputs(values) },
      {
        onSuccess: () => onDone(values),
        onError: (e) => setServerError(mensajeDeError(e, 'guardar los datos del separante')),
      },
    );
  };

  const autorizacionError = formState.errors.autorizacion?.message;

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} aria-labelledby="paso-separante" className={SECTION_CARD_CLASS}>
      <div>
        <p className={EYEBROW_CLASS}>Paso 2</p>
        <h2 id="paso-separante" className="text-xl font-semibold tracking-tight text-foreground">
          Datos del separante
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Si la separación es a nombre de varias personas, agrega cada una con su porcentaje de participación.
        </p>
      </div>

      {fields.map((field, index) => (
        <ApplicantFields
          key={field.id}
          form={form}
          index={index}
          mostrarPorcentaje={varios}
          onRemove={index > 0 ? () => remove(index) : undefined}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full lg:w-auto"
        onClick={() => append(emptyApplicant(''))}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Agregar separante
      </Button>

      <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
        <Controller
          control={control}
          name="autorizacion"
          render={({ field }) => (
            <div className="flex items-start gap-3">
              <Checkbox
                id="sep-autorizacion"
                className="mt-0.5 h-5 w-5"
                checked={field.value}
                onCheckedChange={(v) => field.onChange(v === true)}
                aria-invalid={autorizacionError ? true : undefined}
                aria-describedby={autorizacionError ? 'sep-autorizacion-error' : undefined}
              />
              <label htmlFor="sep-autorizacion" className="cursor-pointer text-sm leading-relaxed text-foreground">
                <ShieldCheck className="mr-1 inline h-4 w-4 text-brand-gold-dark" aria-hidden="true" />
                El separante autoriza el tratamiento de sus datos personales conforme a la Ley 1581 de 2012.
              </label>
            </div>
          )}
        />
        {autorizacionError && (
          <p id="sep-autorizacion-error" role="alert" className="mt-2 text-sm text-destructive">
            {autorizacionError}
          </p>
        )}
      </div>

      {serverError && (
        <p role="alert" className="text-sm text-destructive">
          {serverError}
        </p>
      )}

      <div className="flex justify-end">
        <Button type="submit" size="lg" className="w-full lg:w-auto" loading={guardar.isPending}>
          Continuar a condiciones
        </Button>
      </div>
    </form>
  );
}

interface ApplicantFieldsProps {
  form: UseFormReturn<ApplicantsFormValues>;
  index: number;
  mostrarPorcentaje: boolean;
  onRemove?: () => void;
}

function ApplicantFields({ form, index, mostrarPorcentaje, onRemove }: ApplicantFieldsProps): React.JSX.Element {
  const { register, watch, setValue, formState } = form;
  const errores = formState.errors.separantes?.[index];
  const tipo = watch(`separantes.${index}.tipoPersona`);
  const juridica = tipo === 'JURIDICA';
  const id = (campo: string): string => `sep-${index}-${campo}`;

  const texto = (
    campo:
      | 'nombres'
      | 'apellidos'
      | 'razonSocial'
      | 'numeroDocumento'
      | 'ciudadExpedicion'
      | 'direccion'
      | 'ciudad'
      | 'celular'
      | 'correo',
    label: string,
    extra: React.InputHTMLAttributes<HTMLInputElement> = {},
  ): React.JSX.Element => (
    <FormField label={label} htmlFor={id(campo)} required error={errores?.[campo]?.message}>
      <Input
        id={id(campo)}
        className={TOUCH_INPUT_CLASS}
        error={!!errores?.[campo]}
        {...extra}
        {...register(`separantes.${index}.${campo}`)}
      />
    </FormField>
  );

  const cambiarTipo = (nuevo: 'NATURAL' | 'JURIDICA'): void => {
    setValue(`separantes.${index}.tipoPersona`, nuevo);
    setValue(`separantes.${index}.tipoDocumento`, nuevo === 'JURIDICA' ? 'NIT' : 'CC');
  };

  return (
    <fieldset className="space-y-4 rounded-lg border border-border/70 p-4">
      <legend className="px-1 text-sm font-semibold text-foreground">Separante {index + 1}</legend>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="radiogroup" aria-label="Tipo de persona" className="flex flex-wrap gap-2">
          {(
            [
              ['NATURAL', 'Persona natural'],
              ['JURIDICA', 'Persona jurídica'],
            ] as const
          ).map(([valor, label]) => (
            <label
              key={valor}
              className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-input px-3 text-sm has-[:checked]:border-brand-gold has-[:checked]:bg-gold-sheen has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
            >
              <input
                type="radio"
                name={`separantes.${index}.tipoPersona`}
                value={valor}
                checked={tipo === valor}
                onChange={() => cambiarTipo(valor)}
                className="h-4 w-4 accent-primary"
              />
              {label}
            </label>
          ))}
        </div>
        {onRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label={`Quitar separante ${index + 1}`}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Quitar
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {juridica ? (
          <>
            {texto('razonSocial', 'Razón social', { autoComplete: 'organization' })}
            {texto('numeroDocumento', 'NIT', { inputMode: 'numeric' })}
            {texto('nombres', 'Nombres del representante legal')}
            {texto('apellidos', 'Apellidos del representante legal')}
          </>
        ) : (
          <>
            {texto('nombres', 'Nombres', { autoComplete: 'given-name' })}
            {texto('apellidos', 'Apellidos', { autoComplete: 'family-name' })}
            <FormField label="Tipo de documento" htmlFor={id('tipoDocumento')} required>
              <select id={id('tipoDocumento')} className={NATIVE_SELECT_CLASS} {...register(`separantes.${index}.tipoDocumento`)}>
                {TIPOS_DOCUMENTO_NATURAL.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </FormField>
            {texto('numeroDocumento', 'Número de documento', { inputMode: 'numeric' })}
            {texto('ciudadExpedicion', 'Ciudad de expedición')}
            <FormField label="Estado civil" htmlFor={id('estadoCivil')} required error={errores?.estadoCivil?.message}>
              <select id={id('estadoCivil')} className={NATIVE_SELECT_CLASS} {...register(`separantes.${index}.estadoCivil`)}>
                <option value="">Selecciona</option>
                {ESTADOS_CIVILES.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </FormField>
          </>
        )}
        {texto('direccion', 'Dirección', { autoComplete: 'street-address' })}
        {texto('ciudad', 'Ciudad', { autoComplete: 'address-level2' })}
        {texto('celular', 'Celular', { inputMode: 'tel', autoComplete: 'tel' })}
        {texto('correo', 'Correo', { type: 'email', inputMode: 'email', autoComplete: 'email' })}
        {mostrarPorcentaje && (
          <FormField
            label="Participación (%)"
            htmlFor={id('porcentaje')}
            required
            error={errores?.porcentaje?.message}
          >
            <Input
              id={id('porcentaje')}
              inputMode="numeric"
              className={TOUCH_INPUT_CLASS}
              error={!!errores?.porcentaje}
              {...register(`separantes.${index}.porcentaje`)}
            />
          </FormField>
        )}
      </div>
    </fieldset>
  );
}
